import { useEffect, useState } from 'react'
import { tournamentApi } from '../../services/api'
import { BilingualMarkdownEditor } from '../../components/MarkdownField'

// Placeholders the reseller can drop into the message; filled with the quote's
// customer details when the quote is rendered/sent.
const PLACEHOLDERS = [
  { label: 'First name', token: '{{first_name}}' },
  { label: 'Last name', token: '{{last_name}}' },
  { label: 'Full name', token: '{{full_name}}' },
  { label: 'Email', token: '{{email}}' },
  { label: 'Phone', token: '{{phone}}' },
]

/**
 * CTQ Quote settings — pick the signing profile + language for THIS quote, and
 * manage the user's reusable signing profiles (message EN/FR, planner contact,
 * photo; multiple, with one default). Opened from the builder header.
 *
 * CTQ feature branch — not deployed to production until the feature is done.
 */
const inp = { padding: '7px 9px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' }
const lbl = { fontSize: 11.5, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 3 }
const blankProfile = () => ({ name: '', plannerName: '', plannerTitle: '', plannerEmail: '', plannerPhone: '', photoUrl: '', messageEn: '', messageFr: '' })

export default function QuoteSettingsModal({ quote, onSaveQuote, onClose, t }) {
  const [profiles, setProfiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null) // profile being created/edited, or null
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [err, setErr] = useState(null)

  function load() {
    tournamentApi.get('/portal/reseller/quote-profiles')
      .then(r => setProfiles(r.data.profiles || []))
      .catch(e => setErr(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
  }
  useEffect(load, [])

  const defaultId = (profiles.find(p => p.isDefault) || {}).id
  const selectedId = quote.quoteProfileId || defaultId || ''

  async function saveProfile() {
    setSaving(true); setErr(null)
    try {
      if (editing.id) await tournamentApi.patch(`/portal/reseller/quote-profiles/${editing.id}`, editing)
      else await tournamentApi.post('/portal/reseller/quote-profiles', editing)
      setEditing(null); load()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setSaving(false) }
  }
  async function del(id) {
    if (!window.confirm(t('sp_ctq_delete_profile', 'Delete this profile?'))) return
    try {
      await tournamentApi.delete(`/portal/reseller/quote-profiles/${id}`)
      if (quote.quoteProfileId === id) onSaveQuote({ quoteProfileId: null })
      load()
    } catch (e) { alert(e?.response?.data?.message || e.message) }
  }
  async function makeDefault(id) {
    try { await tournamentApi.post(`/portal/reseller/quote-profiles/${id}/default`); load() }
    catch (e) { alert(e?.response?.data?.message || e.message) }
  }
  async function uploadPhoto(file) {
    if (!file) return
    setUploading(true)
    try {
      const fd = new FormData(); fd.append('image', file)
      const { data } = await tournamentApi.post('/portal/reseller/uploads/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setEditing(p => ({ ...p, photoUrl: data.url }))
    } catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setUploading(false) }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, zIndex: 60, overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 18, width: '100%', maxWidth: 640, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>{t('sp_ctq_quote_settings', 'Quote settings')}</h3>
          <button onClick={onClose} style={{ border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', padding: '6px 12px', cursor: 'pointer' }}>{t('sp_ctq_close', 'Close')}</button>
        </div>

        {err && <div style={{ padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13, marginBottom: 12 }}>{err}</div>}

        {/* This quote */}
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: 0.4, marginBottom: 10 }}>{t('sp_ctq_this_quote', 'THIS QUOTE')}</div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ minWidth: 220, flex: 1 }}>
              <label style={lbl}>{t('sp_ctq_signed_by', 'Signed by')}</label>
              <select value={selectedId} onChange={e => onSaveQuote({ quoteProfileId: e.target.value ? Number(e.target.value) : null })} style={inp}>
                {profiles.length === 0 && <option value="">{t('sp_ctq_no_profiles', 'No profiles yet — create one below')}</option>}
                {profiles.map(p => <option key={p.id} value={p.id}>{(p.name || p.plannerName || `Profile ${p.id}`) + (p.isDefault ? ' · default' : '')}</option>)}
              </select>
            </div>
            <button onClick={() => window.open(`/portal/reseller/custom-trips/${quote.id}/preview`, '_blank')}
              style={{ padding: '8px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer' }}>
              👁 {t('sp_ctq_preview', 'Preview')}
            </button>
          </div>
        </div>

        {/* Profiles */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: 0.4 }}>{t('sp_ctq_your_profiles', 'YOUR SIGNING PROFILES')}</div>
          {!editing && <button onClick={() => setEditing(blankProfile())} style={{ padding: '6px 12px', borderRadius: 6, fontSize: 12.5, fontWeight: 600, border: 'none', background: '#0f172a', color: '#fff', cursor: 'pointer' }}>+ {t('sp_ctq_new_profile', 'New profile')}</button>}
        </div>

        {loading ? <div style={{ color: '#64748b', fontSize: 13, padding: 8 }}>Loading…</div>
          : editing ? <ProfileEditor editing={editing} setEditing={setEditing} onSave={saveProfile} onCancel={() => setEditing(null)} onUpload={uploadPhoto} uploading={uploading} saving={saving} t={t} />
            : (
              <div style={{ display: 'grid', gap: 8 }}>
                {profiles.length === 0 && <div style={{ fontSize: 12.5, color: '#94a3b8', padding: 8 }}>{t('sp_ctq_no_profiles_hint', 'Create a signing profile — your message, contact info, and photo — reused on every quote.')}</div>}
                {profiles.map(p => (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, border: '1px solid #e2e8f0', borderRadius: 8, padding: 10 }}>
                    {p.photoUrl ? <img src={p.photoUrl} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} /> : <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#e2e8f0' }} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: '#0f172a' }}>
                        {p.name || p.plannerName || `Profile ${p.id}`}
                        {p.isDefault && <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 700, color: '#166534', background: '#dcfce7', padding: '2px 7px', borderRadius: 999 }}>DEFAULT</span>}
                      </div>
                      <div style={{ fontSize: 12, color: '#94a3b8' }}>{[p.plannerName, p.plannerTitle].filter(Boolean).join(' · ')}</div>
                    </div>
                    {!p.isDefault && <button onClick={() => makeDefault(p.id)} style={{ border: 'none', background: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 12.5 }}>{t('sp_ctq_set_default', 'set default')}</button>}
                    <button onClick={() => setEditing({ ...p })} style={{ border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', padding: '5px 10px', fontSize: 12.5, cursor: 'pointer' }}>{t('sp_ctq_edit', 'Edit')}</button>
                    <button onClick={() => del(p.id)} title={t('sp_ctq_delete', 'Delete')} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 15 }}>&times;</button>
                  </div>
                ))}
              </div>
            )}
      </div>
    </div>
  )
}

function ProfileEditor({ editing, setEditing, onSave, onCancel, onUpload, uploading, saving, t }) {
  const set = (k) => (e) => setEditing(p => ({ ...p, [k]: e.target.value }))
  const setVal = (k) => (v) => setEditing(p => ({ ...p, [k]: v }))
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>{t('sp_ctq_profile_name', 'Profile name (internal)')}</label><input value={editing.name || ''} onChange={set('name')} placeholder="e.g. Bryce — golf trips" style={inp} /></div>
        <div><label style={lbl}>{t('sp_ctq_planner_name', 'Your name')}</label><input value={editing.plannerName || ''} onChange={set('plannerName')} style={inp} /></div>
        <div><label style={lbl}>{t('sp_ctq_planner_title', 'Title')}</label><input value={editing.plannerTitle || ''} onChange={set('plannerTitle')} placeholder="Trip Planner" style={inp} /></div>
        <div><label style={lbl}>{t('sp_ctq_planner_email', 'Email')}</label><input type="email" value={editing.plannerEmail || ''} onChange={set('plannerEmail')} style={inp} /></div>
        <div><label style={lbl}>{t('sp_ctq_planner_phone', 'Phone')}</label><input value={editing.plannerPhone || ''} onChange={set('plannerPhone')} style={inp} /></div>
        <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 12 }}>
          {editing.photoUrl ? <img src={editing.photoUrl} alt="" style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }} /> : <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#e2e8f0' }} />}
          <label style={{ padding: '7px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12.5, cursor: 'pointer', background: '#fff' }}>
            {uploading ? t('sp_ctq_uploading', 'Uploading…') : (editing.photoUrl ? t('sp_ctq_change_photo', 'Change photo') : t('sp_ctq_add_photo', 'Add photo'))}
            <input type="file" accept="image/*" onChange={e => onUpload(e.target.files?.[0])} style={{ display: 'none' }} />
          </label>
          {editing.photoUrl && <button onClick={() => setEditing(p => ({ ...p, photoUrl: '' }))} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12.5 }}>{t('sp_ctq_remove_photo', 'remove')}</button>}
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={lbl}>{t('sp_ctq_message', 'Message')}</label>
          <BilingualMarkdownEditor
            valueEn={editing.messageEn || ''} valueFr={editing.messageFr || ''}
            onChangeEn={setVal('messageEn')} onChangeFr={setVal('messageFr')}
            insertTokens={PLACEHOLDERS}
            placeholderEn={t('sp_ctq_message_ph', 'Thank you for reaching out, {{first_name}}! Here is your custom trip… (use **bold**, _italic_, and the + buttons for customer details)')}
            placeholderFr={t('sp_ctq_message_fr_ph', 'Merci de nous avoir contactés, {{first_name}}!…')} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button onClick={onSave} disabled={saving} style={{ padding: '8px 16px', borderRadius: 6, fontWeight: 600, fontSize: 13, border: 'none', background: '#0f172a', color: '#fff', cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>{saving ? t('sp_ctq_saving', 'Saving…') : t('sp_ctq_save_profile', 'Save profile')}</button>
        <button onClick={onCancel} style={{ padding: '8px 14px', borderRadius: 6, fontSize: 13, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}>{t('sp_ctq_cancel', 'Cancel')}</button>
      </div>
    </div>
  )
}
